import { env } from '../config/environment';
import { normalizeTimestamp } from '../utils/timestampNormalizer';
import { safeGetItem, safeSetItem } from '../utils/safeStorage';

/**
 * Sistema de otimização de performance para sincronização de dados
 * Implementa cache inteligente, compressão e sincronização incremental
 */

// Interfaces para otimização
interface CacheEntry {
  data: any;
  hash: string;
  timestamp: number;
  size: number;
}

interface SyncMetrics {
  totalSyncs: number;
  successfulSyncs: number;
  failedSyncs: number;
  averageSyncTime: number;
  lastSyncTime: number;
  dataTransferred: number;
}

interface CompressionResult {
  compressed: string;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
}

/**
 * Gerenciador de otimização de sincronização
 */
export class SyncOptimizer {
  private static instance: SyncOptimizer;
  private cache = new Map<string, CacheEntry>();
  private metrics: SyncMetrics = {
    totalSyncs: 0,
    successfulSyncs: 0,
    failedSyncs: 0,
    averageSyncTime: 0,
    lastSyncTime: 0,
    dataTransferred: 0
  };
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutos
  private readonly MAX_CACHE_SIZE = 50; // Máximo de entradas no cache
  private readonly COMPRESSION_THRESHOLD = 1024; // 1KB - comprimir dados maiores que isso

  private constructor() {
    this.loadMetrics();
    this.setupCacheCleanup();
  }

  public static getInstance(): SyncOptimizer {
    if (!SyncOptimizer.instance) {
      SyncOptimizer.instance = new SyncOptimizer();
    }
    return SyncOptimizer.instance;
  }

  /**
   * Verifica se os dados mudaram desde a última sincronização
   */
  public hasDataChanged(key: string, data: any): boolean {
    const hash = this.generateHash(data);
    const cached = this.cache.get(key);
    
    if (!cached) {
      return true; // Primeira vez, considerar como mudança
    }

    // Verificar se o cache expirou
    if (Date.now() - cached.timestamp > this.CACHE_TTL) {
      this.cache.delete(key);
      return true;
    }

    return cached.hash !== hash;
  }

  /**
   * Atualiza o cache com novos dados
   */
  public updateCache(key: string, data: any): void {
    const hash = this.generateHash(data);
    const size = this.calculateDataSize(data);
    
    // Limpar cache se estiver muito grande
    if (this.cache.size >= this.MAX_CACHE_SIZE) {
      this.cleanOldestCacheEntries();
    }

    this.cache.set(key, {
      data: this.shouldCompress(size) ? this.compress(data) : data,
      hash,
      timestamp: Date.now(),
      size
    });

    if (env.features.enableDebugMode) {
      console.log(`Cache atualizado para ${key}, tamanho: ${size} bytes`);
    }
  }

  /**
   * Prepara dados para sincronização otimizada
   */
  public async prepareDataForSync(data: any): Promise<{
    optimizedData: any;
    metadata: {
      compressed: boolean;
      originalSize: number;
      finalSize: number;
      compressionRatio?: number;
    };
  }> {
    const originalSize = this.calculateDataSize(data);
    let optimizedData = data;
    let compressed = false;
    let compressionRatio = 1;

    // Aplicar compressão se necessário
    if (this.shouldCompress(originalSize)) {
      try {
        const compressionResult = this.compress(data);
        optimizedData = compressionResult.compressed;
        compressed = true;
        compressionRatio = compressionResult.compressionRatio;
        
        if (env.features.enableDebugMode) {
          console.log(`Dados comprimidos: ${originalSize} → ${compressionResult.compressedSize} bytes (${(compressionRatio * 100).toFixed(1)}% de redução)`);
        }
      } catch (error) {
        console.warn('Falha na compressão, enviando dados originais:', error);
      }
    }

    // Aplicar otimizações adicionais
    optimizedData = this.optimizeDataStructure(optimizedData);

    const finalSize = this.calculateDataSize(optimizedData);

    return {
      optimizedData,
      metadata: {
        compressed,
        originalSize,
        finalSize,
        compressionRatio: compressed ? compressionRatio : undefined
      }
    };
  }

  /**
   * Registra métricas de sincronização
   */
  public recordSyncMetrics(success: boolean, duration: number, dataSize: number): void {
    this.metrics.totalSyncs++;
    this.metrics.lastSyncTime = Date.now();
    this.metrics.dataTransferred += dataSize;

    if (success) {
      this.metrics.successfulSyncs++;
    } else {
      this.metrics.failedSyncs++;
    }

    // Calcular média móvel do tempo de sincronização
    this.metrics.averageSyncTime = (
      (this.metrics.averageSyncTime * (this.metrics.totalSyncs - 1) + duration) / 
      this.metrics.totalSyncs
    );

    this.saveMetrics();

    if (env.features.enableDebugMode) {
      console.log(`Sync metrics - Success: ${success}, Duration: ${duration}ms, Data: ${dataSize} bytes`);
    }
  }

  /**
   * Obtém métricas de performance
   */
  public getMetrics(): SyncMetrics {
    return { ...this.metrics };
  }

  /**
   * Determina a estratégia de sincronização baseada nas métricas
   */
  public getSyncStrategy(): {
    useCompression: boolean;
    batchSize: number;
    retryDelay: number;
    priority: 'high' | 'medium' | 'low';
  } {
    const successRate = this.metrics.totalSyncs > 0 ? 
      this.metrics.successfulSyncs / this.metrics.totalSyncs : 1;
    
    const avgDataSize = this.metrics.totalSyncs > 0 ? 
      this.metrics.dataTransferred / this.metrics.totalSyncs : 0;

    return {
      useCompression: avgDataSize > this.COMPRESSION_THRESHOLD,
      batchSize: successRate > 0.9 ? 100 : 50, // Reduzir batch se muitas falhas
      retryDelay: this.metrics.averageSyncTime * 2, // Baseado no tempo médio
      priority: successRate > 0.95 ? 'high' : successRate > 0.8 ? 'medium' : 'low'
    };
  }

  /**
   * Limpa o cache e reseta métricas
   */
  public reset(): void {
    this.cache.clear();
    this.metrics = {
      totalSyncs: 0,
      successfulSyncs: 0,
      failedSyncs: 0,
      averageSyncTime: 0,
      lastSyncTime: 0,
      dataTransferred: 0
    };
    this.saveMetrics();
    
    if (env.features.enableDebugMode) {
      console.log('SyncOptimizer resetado');
    }
  }

  // Métodos privados

  private generateHash(data: any): string {
    const str = JSON.stringify(data, Object.keys(data).sort());
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Converter para 32bit
    }
    return hash.toString(36);
  }

  private calculateDataSize(data: any): number {
    return new Blob([JSON.stringify(data)]).size;
  }

  private shouldCompress(size: number): boolean {
    return size > this.COMPRESSION_THRESHOLD;
  }

  private compress(data: any): CompressionResult {
    const originalStr = JSON.stringify(data);
    const originalSize = originalStr.length;
    
    // Implementação simples de compressão usando LZ-string like algorithm
    const compressed = this.simpleCompress(originalStr);
    const compressedSize = compressed.length;
    
    return {
      compressed,
      originalSize,
      compressedSize,
      compressionRatio: (originalSize - compressedSize) / originalSize
    };
  }

  private simpleCompress(str: string): string {
    // Implementação básica de compressão
    // Em produção, usar uma biblioteca como pako ou lz-string
    const dict: { [key: string]: number } = {};
    let dictSize = 256;
    let result = [];
    let w = '';
    
    for (let i = 0; i < str.length; i++) {
      const c = str[i];
      const wc = w + c;
      
      if (dict[wc]) {
        w = wc;
      } else {
        result.push(dict[w] || w.charCodeAt(0));
        dict[wc] = dictSize++;
        w = c;
      }
    }
    
    if (w) {
      result.push(dict[w] || w.charCodeAt(0));
    }
    
    return JSON.stringify(result);
  }

  private optimizeDataStructure(data: any): any {
    // Remover campos desnecessários e otimizar estrutura
    if (Array.isArray(data)) {
      return data.map(item => this.optimizeItem(item));
    } else if (typeof data === 'object' && data !== null) {
      return this.optimizeItem(data);
    }
    return data;
  }

  private optimizeItem(item: any): any {
    if (typeof item !== 'object' || item === null) {
      return item;
    }

    const optimized: any = {};
    
    // Remover campos vazios ou desnecessários
    for (const [key, value] of Object.entries(item)) {
      if (value !== null && value !== undefined && value !== '') {
        // Otimizar timestamps
        if (key.includes('date') || key.includes('time') || key.includes('created') || key.includes('updated')) {
          optimized[key] = this.optimizeTimestamp(value);
        } else {
          optimized[key] = value;
        }
      }
    }

    return optimized;
  }

  private optimizeTimestamp(value: any): string {
    return normalizeTimestamp(value);
  }

  private cleanOldestCacheEntries(): void {
    const entries = Array.from(this.cache.entries())
      .sort(([, a], [, b]) => a.timestamp - b.timestamp);
    
    // Remover 25% das entradas mais antigas
    const toRemove = Math.ceil(entries.length * 0.25);
    for (let i = 0; i < toRemove; i++) {
      this.cache.delete(entries[i][0]);
    }

    if (env.features.enableDebugMode) {
      console.log(`Cache limpo: ${toRemove} entradas removidas`);
    }
  }

  private setupCacheCleanup(): void {
    // Limpeza automática do cache a cada 10 minutos
    setInterval(() => {
      const now = Date.now();
      let cleaned = 0;
      
      Array.from(this.cache.entries()).forEach(([key, entry]) => {
        if (now - entry.timestamp > this.CACHE_TTL) {
          this.cache.delete(key);
          cleaned++;
        }
      });
      
      if (cleaned > 0 && env.features.enableDebugMode) {
        console.log(`Cache cleanup: ${cleaned} entradas expiradas removidas`);
      }
    }, 10 * 60 * 1000);
  }

  private loadMetrics(): void {
    try {
      const stored = safeGetItem('psicosys_sync_metrics');
      if (stored) {
        this.metrics = { ...this.metrics, ...JSON.parse(stored) };
      }
    } catch (error) {
      if (env.features.enableDebugMode) {
        console.warn('Erro ao carregar métricas de sincronização:', error);
      }
    }
  }

  private saveMetrics(): void {
    try {
      safeSetItem('psicosys_sync_metrics', JSON.stringify(this.metrics));
    } catch (error) {
      if (env.features.enableDebugMode) {
        console.warn('Erro ao salvar métricas de sincronização:', error);
      }
    }
  }
}

// Instância singleton
export const syncOptimizer = SyncOptimizer.getInstance();

// Funções utilitárias exportadas
export const hasDataChanged = (key: string, data: any) => syncOptimizer.hasDataChanged(key, data);
export const updateCache = (key: string, data: any) => syncOptimizer.updateCache(key, data);
export const prepareDataForSync = (data: any) => syncOptimizer.prepareDataForSync(data);
export const recordSyncMetrics = (success: boolean, duration: number, dataSize: number) => 
  syncOptimizer.recordSyncMetrics(success, duration, dataSize);
export const getSyncMetrics = () => syncOptimizer.getMetrics();
export const getSyncStrategy = () => syncOptimizer.getSyncStrategy();
export const resetSyncOptimizer = () => syncOptimizer.reset();